import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";

export default function AuthVerify() {
  const [params] = useSearchParams();
  const t = useT();
  const navigate = useNavigate();
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState("error");
      setErrMsg(t("magic.verify.error"));
      return;
    }
    (async () => {
      try {
        // The backend sets the httpOnly access_token cookie on success.
        const { data } = await api.post("/auth/magic-verify", { token });
        setState("success");
        const dest =
          data?.user?.role === "admin" ? "/admin" :
          data?.user?.role === "attorney" ? "/attorney" :
          "/my-cases";
        setTimeout(() => navigate(dest, { replace: true }), 900);
      } catch (e) {
        setState("error");
        setErrMsg(formatApiError(e.response?.data?.detail) || t("magic.verify.error"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen grid place-items-center px-6" data-testid="auth-verify-page">
      <div className="card-soft w-full max-w-md p-10 text-center">
        {state === "verifying" && (
          <>
            <Loader2 className="mx-auto h-9 w-9 animate-spin text-[#11322A]" data-testid="magic-verify-loader" />
            <h1 className="mt-5 font-display text-2xl font-black text-[#11322A]">
              {t("magic.verify.title")}
            </h1>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#2D6A4F]" />
            <h1 className="mt-5 font-display text-2xl font-black text-[#11322A]">
              {t("magic.verify.success")}
            </h1>
          </>
        )}
        {state === "error" && (
          <>
            <AlertTriangle className="mx-auto h-12 w-12 text-[#B91C1C]" />
            <p className="mt-5 text-sm font-semibold text-[#0B1317]" data-testid="magic-verify-error">
              {errMsg}
            </p>
            <Link to="/login" className="btn-primary mt-6 inline-flex" data-testid="magic-verify-back">
              {t("magic.verify.cta")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
